// Lib
import React, { useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useForm, Controller } from 'react-hook-form';
import MaskedInput from 'react-input-mask';

// Components
import Input from '../../Field/Input';
import Select from '../../Field/Select';

// Validation schema
import { newAddressSchema } from '../../../validationSchema';

// Hooks
import useUI from '../../../hooks/useUI';

// Styles
import { useStyles } from './FormStyles';

const getFieldError = (fieldError, apiFieldError) =>
  fieldError?.message || apiFieldError?.[0];
const withDefault = (value, fallback) => value || fallback;
const Form = ({
  address = {},
  onChangeSelect,
  onChangeInput,
  states,
  countries,
  apiErrors,
  uspsError,
  onSubmit,
  children,
  hideElement = () => {},
  showElement = () => {},
}) => {
  const { classes, cx } = useStyles();
  const { isMobileMode } = useUI();

  const {
    address1,
    address2,
    address_type: addressType,
    city,
    company,
    country,
    first_name: firstName,
    last_name: lastName,
    phone,
    state,
    zip,
  } = address;

  const fullState = useMemo(() => {
    if (!state) {
      return;
    }
    const { name } = states.find((item) => item.isoCode === state) || {};
    return name ? name : state;
  }, [states, state]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(newAddressSchema),
    defaultValues: {
      addressType: withDefault(addressType, ''),
      company: withDefault(company, ''),
      firstName: withDefault(firstName, ''),
      lastName: withDefault(lastName, ''),
      phone: withDefault(phone, ''),
      address1: withDefault(address1, ''),
      address2: withDefault(address2, ''),
      country: withDefault(country, 'United States'),
      state: withDefault(fullState, ''),
      city: withDefault(city, ''),
      zip: withDefault(zip, ''),
    },
  });

  const currentCountry = watch('country');

  useEffect(() => {
    if (!isMobileMode) {
      return undefined;
    }
    const inputs = document.querySelectorAll('input');
    inputs.forEach((item) => {
      item.addEventListener('focus', hideElement);
      item.addEventListener('blur', showElement);
    });
    return () => {
      inputs.forEach((item) => {
        item.removeEventListener('focus', hideElement);
        item.removeEventListener('blur', showElement);
      });
    };
  }, [isMobileMode, hideElement, showElement]);

  const renderUSPSError = () => {
    return uspsError && <div className={classes.uspsError}>{uspsError}</div>;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {renderUSPSError()}
      <Controller
        name="addressType"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            maxLength={100}
            onChange={onChangeInput(field.onChange)}
            error={getFieldError(errors?.addressType, apiErrors?.address_type)}
            className={cx(classes.input, classes.addressType)}
            label="Address Type"
            required
            data-testid="address-type-input"
          />
        )}
      />
      <Controller
        name="company"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            maxLength={35}
            onChange={onChangeInput(field.onChange)}
            className={classes.input}
            label="Company"
            data-testid="address-company-input"
          />
        )}
      />
      <div className={classes.inputContainer}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => {
            return (
              <Input
                {...field}
                maxLength={35}
                onChange={onChangeInput(field.onChange)}
                error={getFieldError(errors?.firstName)}
                className={classes.input}
                label="First Name"
                data-testid="address-first-name-input"
              />
            );
          }}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              maxLength={35}
              onChange={onChangeInput(field.onChange)}
              error={getFieldError(errors?.lastName, apiErrors?.last_name)}
              className={classes.input}
              required
              label="Last Name"
              data-testid="address-last-name-input"
            />
          )}
        />
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <MaskedInput
              mask="999-999-9999"
              maskChar=""
              onChange={onChangeInput(field.onChange, true)}
              value={field.value}>
              {(inputProps) => {
                return (
                  <Input
                    name="phone"
                    placeholder="555-555-5555"
                    {...inputProps}
                    withMask
                    maxLength={15}
                    error={getFieldError(errors?.phone, apiErrors?.phone)}
                    className={classes.input}
                    required
                    label="Phone"
                    data-testid="address-phone-input"
                  />
                );
              }}
            </MaskedInput>
          )}
        />
      </div>
      <Controller
        name="address1"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            maxLength={35}
            onChange={onChangeInput(field.onChange)}
            error={getFieldError(errors?.address1, apiErrors?.address1)}
            className={classes.input}
            required
            label="Address 1"
            placeholder="No P.O. Boxes Allowed"
            data-testid="address-one-input"
          />
        )}
      />
      <Controller
        name="address2"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            placeholder="No P.O. Boxes Allowed"
            maxLength={35}
            onChange={onChangeInput(field.onChange)}
            error={getFieldError(errors?.address2)}
            className={classes.input}
            label="Address 2"
            data-testid="address-two-input"
          />
        )}
      />
      <div className={classes.inputContainer}>
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <Select
              initialValue={withDefault(address.country, 'United States')}
              {...field}
              error={getFieldError(errors?.country, apiErrors?.country)}
              onChange={onChangeSelect(field.onChange, setValue, field)}
              className={classes.select}
              classic
              label="Country"
              required
              items={countries}
              data-testid="address-country-select"
            />
          )}
        />
        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <Select
              withRemoveIcon={apiErrors?.state?.[0]?.includes('not required')}
              {...field}
              initialValue={field.value}
              disabled={currentCountry !== 'United States'}
              error={getFieldError(
                currentCountry === 'United States' ? errors?.state : undefined,
                apiErrors?.state,
              )}
              onChange={onChangeSelect(field.onChange, undefined, field)}
              placeholder="Select"
              items={states}
              className={classes.select}
              classic
              label="State"
              required={currentCountry === 'United States'}
              data-testid="address-state-select"
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              maxLength={30}
              onChange={onChangeInput(field.onChange)}
              error={getFieldError(errors?.city, apiErrors?.city)}
              className={classes.input}
              required
              label="City"
              data-testid="address-city-input"
            />
          )}
        />
        <Controller
          name="zip"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              maxLength={10}
              onChange={onChangeInput(field.onChange)}
              error={getFieldError(errors?.zip, apiErrors?.zip)}
              className={cx(classes.input, classes.zip)}
              required
              label="Zip"
              data-testid="address-zip-input"
            />
          )}
        />
      </div>
      {children}
    </form>
  );
};

export default Form;
